'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getBookingUrl } from '@/lib/booking';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function TestimonialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).home.testimonials;
  const bookingHref = getBookingUrl('after_testimonials');
  const bookingExternal = bookingHref.startsWith('http');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="border-b border-border bg-background px-6 py-16 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-3xl text-center">
        <div
          className={`mb-6 transition-all duration-800 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
            {copy.eyebrow}
          </span>
          <div className="mx-auto mt-2 h-[2px] w-12 bg-gold" />
        </div>
        <h2
          className={`mb-8 font-heading text-4xl font-bold leading-tight text-foreground transition-all duration-800 delay-100 md:text-6xl ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          {copy.title} <span className="italic text-gold">{copy.titleItalic}</span>
        </h2>
        <div
          className={`space-y-6 text-lg font-light leading-relaxed text-foreground/80 transition-all duration-800 delay-200 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <p>{copy.p1}</p>
          <p className="text-base text-muted-foreground">{copy.p2}</p>
        </div>

        <div
          className={`mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row transition-all duration-800 delay-300 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <a
            href={bookingHref}
            {...(bookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 bg-gold px-10 py-4 text-xs font-black uppercase tracking-[0.18em] text-gold-foreground transition-colors hover:bg-gold/90"
          >
            <Icon name="CalendarDaysIcon" variant="solid" size={18} />
            {copy.primaryCta}
          </a>
          <a
            href={localizePath('/#contact')}
            className="inline-flex min-h-[48px] items-center justify-center border-2 border-primary px-10 py-4 text-xs font-black uppercase tracking-[0.18em] text-primary transition-colors hover:bg-primary hover:text-white"
          >
            {copy.secondaryCta}
          </a>
        </div>
      </div>
    </section>
  );
}
